import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import "../css/AppointmentScreen.css";
import PageTitle from "./PageTitleCompoent";
import AppointmentFilter from "../components/AppointmentFilterComponent";
import AppointmentStatCard from "../components/AppointmentStatCard";
import AppointmentCard from "../components/AppointmentCard";
import { motion, AnimatePresence } from "framer-motion";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../api"; // centralized axios instance
import { GoPlus } from "react-icons/go";
import PawAnimation from "./PawAnimation";

const statusLabels = {
  new: "New Appointments",
  assigned: "Assigned Appointments",
  ongoing: "Ongoing Appointments",
  completed: "Completed Appointments",
  clinic: "Clinic Visits",
  home: "Home Visits",
  online: "Online Consultations",
};

const filterOptions = [
  { id: "newAppointments", label: "New Appointments", status: "new" },
  { id: "assignedAppointments", label: "Assigned Appointments", status: "assigned" },
  { id: "upcomingAppointments", label: "Upcoming Appointments", status: "ongoing" },
  { id: "completedAppointments", label: "Completed Appointments", status: "completed" },
  { id: "clinicVisit", label: "Clinic Visit", type: "clinical_visit" },
  { id: "homeVisit", label: "Home Visit", type: "home_visit" },
  { id: "onlineConsultation", label: "Online Consultation", type: "video_call" },
];

export default function AppointmentScreen() {
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    newAppointments: false,
    assignedAppointments: false,
    upcomingAppointments: false,
    completedAppointments: false,
    clinicVisit: false,
    homeVisit: false,
    onlineConsultation: false,
  });

  const [appointments, setAppointments] = useState([]);
  const [statsData, setStatsData] = useState([]);

  const handleFilterChange = (id, checked) => {
    setFilters((prev) => ({ ...prev, [id]: checked }));
    setViewMode("row"); // switch to row when filter changes
  };

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/admin/doctor-appointments/");
        console.log("Fetched appointments data:", data);

        const stats = [
          {
            title: "Total Appointments",
            amount: `₹${data.package_analytics.overall.total_amount}`,
            count: data.summary.total,
            percent: `${data.package_analytics.overall.completion_percentage}%`,
            color: "#a44af3",
          },
          {
            title: "Online Consultation",
            amount: `₹${data.package_analytics.video_call.total_amount}`,
            count: data.package_analytics.video_call.total_appointments,
            percent: `${data.package_analytics.video_call.completion_percentage}%`,
            color: "#2097f3",
          },
          {
            title: "Clinic Visit",
            amount: `₹${data.package_analytics.clinical_visit.total_amount}`,
            count: data.package_analytics.clinical_visit.total_appointments,
            percent: `${data.package_analytics.clinical_visit.completion_percentage}%`,
            color: "#f3b32a",
          },
          {
            title: "Home Visit",
            amount: `₹${data.package_analytics.home_visit.total_amount}`,
            count: data.package_analytics.home_visit.total_appointments,
            percent: `${data.package_analytics.home_visit.completion_percentage}%`,
            color: "#3cba4d",
          },
        ];

        setStatsData(stats);

        const mergedAppointments = [
          ...(data.new || []),
          ...(data.assigned || []),
          ...(data.ongoing || []),
          ...(data.completed || []),
        ].map((appt) => ({
          doctor: {
            name: appt.customer_name,
            avatar: appt.customer_profile_image
              ? `${api.defaults.baseURL.split("/api")[0]}${
                  appt.customer_profile_image.startsWith("/")
                    ? appt.customer_profile_image
                    : `/${appt.customer_profile_image}`
                }`
              : "",
          },
          type: appt.package_type || appt.package_type,
          date: appt.appointment_date,
          time: appt.appointment_time,
          amount: `₹${appt.payment_details?.amount || 0}`,
          status: appt.status,
          assignedTo: appt.doctor?.name || appt.assigned_to || "NA",
          appointmentData: appt,
        }));

        setAppointments(mergedAppointments);
      } catch (error) {
        console.error("Error fetching appointments:", error);
        toast.error("Failed to load appointments.");
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, []);

  const selectedStatuses = filterOptions
    .filter((opt) => opt.status && filters[opt.id])
    .map((opt) => opt.status);

  const selectedTypes = filterOptions
    .filter((opt) => opt.type && filters[opt.id])
    .map((opt) => opt.type);

  const filteredAppointments = appointments.filter((appointment) => {
    const matchStatus = selectedStatuses.length
      ? selectedStatuses.includes(appointment.status)
      : true;
    const matchType = selectedTypes.length
      ? selectedTypes.includes(appointment.type)
      : true;
    return matchStatus && matchType;
  });

  // ✅ Updated handleAccept to update the specific appointment locally
  const handleAccept = (doctorName, appointmentId) => {
    setAppointments((prev) =>
      prev.map((appt) =>
        appt.appointmentData.id === appointmentId
          ? { ...appt, assignedTo: doctorName, status: "assigned" }
          : appt
      )
    );
    toast.success(`Your appointment with ${doctorName} has been accepted.`, {
      autoClose: 2000,
    });
  };

  const handleReject = (doctorName) => {
    toast.error(`Your appointment with ${doctorName} has been rejected.`, {
      autoClose: 3000,position: "bottom-left"
    });
  };

  if (loading) {
    return (
      <AnimatePresence>
        <motion.div
          className="full-screen-loader"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <PawAnimation />
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <div className="appointment-container">
      <Sidebar />
      <div className="appointment-main">
        <Header />

        <div className="appointment-content">
          <div className="appointment-heading-row">
            <PageTitle title="Appointments" />

            <div className="appointment-actions">
              <div className="appointment-add-btn">
                <button className="appointment-add-button">
                  <span>
                    <GoPlus style={{ width: "25px", height: "15px" }} />
                    Add Appointment
                  </span>
                </button>
              </div>

              <button
                className={`appointment-filter-toggle ${
                  showFilters ? "active" : ""
                }`}
                onClick={() => setShowFilters((prev) => !prev)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="30"
                  height="30"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="#EBA8BF"
                    d="M6.532 4.75h6.936c.457 0 .854 0 1.165.03c.307.028.685.095.993.348c.397.326.621.814.624 1.322c.002.39-.172.726-.34.992c-.168.27-.411.59-.695.964l-.031.04l-.01.013l-2.555 3.369c-.252.332-.315.42-.359.51a1.2 1.2 0 0 0-.099.297c-.02.1-.023.212-.023.634v4.243c0 .208 0 .412-.014.578c-.015.164-.052.427-.224.663c-.21.287-.537.473-.9.495c-.302.019-.547-.103-.69-.183c-.144-.08-.309-.195-.476-.31l-.989-.683l-.048-.033c-.191-.131-.403-.276-.562-.477a1.7 1.7 0 0 1-.303-.585c-.071-.244-.07-.5-.07-.738v-2.97c0-.422-.004-.534-.023-.634a1.2 1.2 0 0 0-.1-.297c-.043-.09-.106-.178-.358-.51L4.825 8.459l-.01-.012l-.03-.04c-.284-.375-.527-.695-.696-.965c-.167-.266-.34-.602-.339-.992a1.72 1.72 0 0 1 .624-1.322c.308-.253.686-.32.993-.349c.311-.029.707-.029 1.165-.029m.397 4l1.647 2.17l.035.047c.201.264.361.475.478.715q.154.317.222.665c.051.261.05.527.05.864v2.968c0 .158.001.247.005.314l.006.062a.2.2 0 0 0 .036.073l.041.034c.05.04.12.088.248.176l.941.65V13.21c0-.337 0-.603.051-.864q.068-.347.222-.665c.117-.24.277-.45.478-.715l.035-.046l1.646-2.17zm7.28-1.5c.195-.26.334-.45.43-.604c.08-.126.104-.188.11-.207a.22.22 0 0 0-.057-.134a1 1 0 0 0-.2-.032c-.232-.022-.556-.023-1.06-.023H6.568c-.504 0-.828 0-1.06.023a1 1 0 0 0-.2.032a.22.22 0 0 0-.057.134c.006.019.03.081.11.207c.096.155.235.344.43.604zm1.541 3.25a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 0 1.5h-3a.75.75 0 0 1-.75-.75m-1.5 2.5a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 0 1.5H15a.75.75 0 0 1-.75-.75m-.5 2.5a.75.75 0 0 1 .75-.75h5a.75.75 0 0 1 0 1.5h-5a.75.75 0 0 1-.75-.75m0 2.5a.75.75 0 0 1 .75-.75H17a.75.75 0 0 1 0 1.5h-2.5a.75.75 0 0 1-.75-.75"
                  />
                </svg>
              </button>
            </div>
          </div>

          {showFilters && (
            <AppointmentFilter
              showFilters={showFilters}
              setShowFilters={setShowFilters}
              filters={filters}
              onFilterChange={handleFilterChange}
            />
          )}

          <div className="appointment-stats-section">
            {statsData.map((stats, index) => (
              <AppointmentStatCard key={index} {...stats} />
            ))}
          </div>

          <div
            className={`appointment-grid ${
              viewMode === "grid" ? "grid-view" : "row-view"
            }`}
          >
            {viewMode === "grid" ? (
              ["new", "assigned", "ongoing", "completed", "clinic", "home", "online"]
                .filter((statusKey) =>
                  filteredAppointments.some((appt) => appt.status === statusKey)
                )
                .map((statusKey) => (
                  <motion.div className="appointment-column" key={statusKey}>
                    <h2 className="appointment-column-title">
                      {statusLabels[statusKey]}
                    </h2>
                    <div className="appointment-card-row">
                      {filteredAppointments
                        .filter((appt) => appt.status === statusKey)
                        .map((appointment, index) => (
                          <motion.div
                            key={index}
                            className="appointment-card-wrapper"
                          >
                            <AppointmentCard
                              doctor={appointment.doctor}
                              type={appointment.type}
                              amount={appointment.amount}
                              date={appointment.date}
                              time={appointment.time}
                              status={appointment.status}
                              assignedTo={appointment.assignedTo}
                              onReject={() =>
                                handleReject(appointment.doctor.name)
                              }
                              onAccept={() =>
                                handleAccept(
                                  appointment.doctor.name,
                                  appointment.appointmentData.id
                                )
                              }
                              appointmentData={appointment.appointmentData}
                            />
                          </motion.div>
                        ))}
                    </div>
                  </motion.div>
                ))
            ) : (
              <>
                <h2 className="appointment-column-title">
                  {filteredAppointments.length === 0
                    ? "No Appointments"
                    : [
                        ...new Set(
                          filteredAppointments.map(
                            (appt) => statusLabels[appt.status]
                          )
                        ),
                      ].join(", ")}
                </h2>
                <AnimatePresence>
                  {filteredAppointments.map((appointment, index) => (
                    <motion.div key={index}>
                      <AppointmentCard
                        className="appointment-card-wrapper"
                        doctor={appointment.doctor}
                        type={appointment.package_name}
                        amount={appointment.amount}
                        date={appointment.date}
                        time={appointment.time}
                        status={appointment.status}
                        assignedTo={appointment.assignedTo}
                        onReject={() => handleReject(appointment.doctor.name)}
                        onAccept={() =>
                          handleAccept(
                            appointment.doctor.name,
                            appointment.appointmentData.id
                          )
                        }
                        appointmentData={appointment.appointmentData}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </>
            )}
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}
